// Extracted from web/components/WorkbenchDialogs.tsx; behavior and declaration bodies preserved.
import { useEffect, useMemo, useState } from "react";

import { ArrowDownToLine } from "lucide-react";
import type { Diagnostic, Dialect, ModelDocument } from "../../shared/contracts";

import { exportMongo, exportNative, generateDDL, generateSemanticSQL } from "../../engineering";

import { downloadBlob } from "../api";
import { dictionarySheets, exportXLSX } from "../../engineering/xlsx";
import type { DictionaryOptions } from "../../engineering/xlsx";

import { ddlExportRoots, selectDDLExport } from "../../engineering/exportSelection";

import { Button, Diagnostics, Field, LossSummary, Modal, Notice, dialectLabel, downloadText, errorText } from "../components/Primitives";

import { dialects } from "../foundation/modelOptions.js";
export function ExportDialog({
  document,
  diagramId,
  selectedIds,
  revision,
  onClose,
}: {
  document: ModelDocument;
  diagramId?: string;
  selectedIds?: readonly string[];
  revision?: number;
  onClose: () => void;
}) {
  const [format, setFormat] = useState("native");
  const [ddlSelection, setDdlSelection] = useState<string[]>();
  const [ddlAcknowledged, setDdlAcknowledged] = useState(false);
  const ddlRoots = useMemo(() => ddlExportRoots(document), [document]);
  const ddlSubset = useMemo(() => ddlSelection ? selectDDLExport(document, ddlSelection) : undefined, [document, ddlSelection]);
  useEffect(() => { setDdlAcknowledged(false); }, [document]);
  const [scope, setScope] = useState<DictionaryOptions['scope']>('model');
  const [exportedAt] = useState(() => new Date().toISOString());
  const activeDiagram = document.diagrams.find(diagram => diagram.diagramId === diagramId);
  const dictionaryOptions = useMemo(() => ({ scope, diagramId, selectedIds, revision, exportedAt, representationId: activeDiagram?.representationId }), [scope, diagramId, selectedIds, revision, exportedAt, activeDiagram?.representationId]);
  const dictionary = useMemo(() => format === 'xlsx' ? dictionarySheets(document, dictionaryOptions) : undefined, [format, document, dictionaryOptions]);
  const [dialect, setDialect] = useState<Dialect>(
    document.model.targetProfile || "postgresql",
  );
  const result = useMemo(() => {
    try {
      if (format === 'xlsx') return { text: '', bytes: exportXLSX(document, dictionaryOptions), diagnostics: [], extension: 'xlsx' };
      if (format === "native")
        return {
          text: exportNative(document),
          diagnostics: [],
          extension: "dataglow.json",
        };
      if (format === "ddl") {
        const r = generateDDL(ddlSubset?.document ?? document, dialect);
        if (ddlSubset) {
          r.lossReport.lost.push(...ddlSubset.omittedRoots.map(object => `${object.name}: excluded from this export`), ...ddlSubset.omittedRelationships.map(relationship => `${relationship.name}: excluded because an endpoint is outside this export`));
          if (!ddlSubset.document.objects.length) r.diagnostics.push({ code: 'DDL_EMPTY_SELECTION', severity: 'error', message: 'Select at least one supported object for export.' });
        }
        return { ...r, text: r.sql, extension: "sql" };
      }
      if (format === "mongo") {
        const r = exportMongo(document);
        return { ...r, text: r.json, extension: "schema.json" };
      }
      const r = generateSemanticSQL(document);
      return { ...r, text: r.sql, extension: "sql" };
    } catch (e) {
      return {
        text: "",
        extension: "txt",
        diagnostics: [
          { code: "EXPORT_ERROR", severity: "error", message: errorText(e) },
        ],
      };
    }
  }, [format, dialect, document, dictionaryOptions, ddlSubset]);
  const blocked = result.diagnostics?.some((d: any) => d.severity === "error") || (format === 'ddl' && !!ddlSubset?.omittedRoots.length && !ddlAcknowledged);
  return (
    <Modal
      title="Export model"
      subtitle="Choose a portable artifact or inspect generated database text."
      wide
      onClose={onClose}
      footer={
        <>
          <span className="footer-note">
            Export never changes a source database.
          </span>
          <Button onClick={onClose}>Close</Button>
          <Button
            icon={ArrowDownToLine}
            variant="primary"
            disabled={blocked || (!result.text && !('bytes' in result && result.bytes))}
            onClick={() => 'bytes' in result && result.bytes ? downloadBlob(`${document.model.name.replace(/[^a-z0-9_-]/gi, '_')}.xlsx`, new Blob([new Uint8Array(result.bytes)], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })) :
              downloadText(
                `${document.model.name.replace(/[^a-z0-9_-]/gi, "_")}.${result.extension}`,
                result.text,
                format === "native" ? "application/json" : "text/plain",
              )
            }
          >
            Download {format === "native" ? "model" : format === 'xlsx' ? 'XLSX' : "output"}
          </Button>
        </>
      }
    >
      <div className="form-row">
        <Field label="Export format">
          <select value={format} onChange={(e) => setFormat(e.target.value)}>
            <option value="native">
              DataGlow model JSON · full-fidelity package
            </option>
            <option value="xlsx">Excel data dictionary (.xlsx)</option>
            {document.model.family === "relational" && (
              <option value="ddl">Database DDL</option>
            )}
            {document.model.family === "document" && (
              <option value="mongo">MongoDB schema</option>
            )}
            {["semantic", "analytical"].includes(document.model.family) && (
              <option value="semantic">Semantic SQL</option>
            )}
          </select>
        </Field>
        {format === "ddl" && (
          <Field label="Target dialect">
            <select
              value={dialect}
              onChange={(e) => { setDialect(e.target.value as Dialect); setDdlAcknowledged(false); }}
            >
              {dialects.map((d) => (
                <option key={d} value={d}>
                  {dialectLabel(d)}
                </option>
              ))}
            </select>
          </Field>
        )}
      </div>
      {format === 'ddl' && !!ddlRoots.length && <details open={!!ddlSubset || result.diagnostics.some(d => d.severity === 'error')}><summary>Choose objects for DDL export ({ddlSelection?.length ?? ddlRoots.length} of {ddlRoots.length})</summary>
        <p>Exclude objects with unresolved generation errors to review a valid subset. Selected objects keep all of their fields, keys and indexes. Dependencies are revalidated after every change.</p>
        {ddlRoots.map(object => <label className="check-row" key={object.objectId}><input type="checkbox" aria-label={`Export ${object.schema ? object.schema + '.' : ''}${object.name}`} checked={ddlSelection ? ddlSelection.includes(object.objectId) : true} onChange={event => {
          const ids = ddlSelection ?? ddlRoots.map(root => root.objectId);
          setDdlSelection(event.target.checked ? [...ids, object.objectId] : ids.filter(id => id !== object.objectId)); setDdlAcknowledged(false);
        }}/>{object.schema ? object.schema + '.' : ''}{object.name} <small>{object.kind}</small></label>)}
        {!!ddlSubset?.omittedRoots.length && <><Notice>{ddlSubset.omittedRoots.length} objects and their descendants omitted. {ddlSubset.omittedRelationships.length} relationships omitted because an endpoint is outside the selected subset. Unsupported dependencies in the remaining selection still block download.</Notice><label className="check-row"><input type="checkbox" checked={ddlAcknowledged} onChange={event => setDdlAcknowledged(event.target.checked)}/>I reviewed the selected DDL objects, omitted relationships and dependency report.</label></>}
      </details>}
      {format === 'xlsx' && <><Field label="Dictionary scope"><select aria-label="Dictionary scope" value={scope} onChange={e => setScope(e.target.value as DictionaryOptions['scope'])}>
        <option value="model">Whole model · all representations</option><option value="representation">Active representation</option>
        {activeDiagram && <option value="diagram">{activeDiagram.role === 'subject-area' ? 'Subject area' : activeDiagram.role === 'view' ? 'View' : 'Active diagram'} · {activeDiagram.name}</option>}
        {!!selectedIds?.length && <option value="selection">Selected objects · full metadata</option>}
      </select></Field><Notice>{dictionary?.summary}. Includes every attribute in scope, independent of canvas display limits.</Notice>
      <table className="dictionary-sheet-preview"><thead><tr><th>Worksheet</th><th>Data rows</th></tr></thead><tbody>{dictionary?.sheets.map(sheet => <tr key={sheet.name}><td>{sheet.name}</td><td>{sheet.rows.length - 1}</td></tr>)}</tbody></table></>}
      <Diagnostics items={(result.diagnostics as Diagnostic[]) || []} />
      <LossSummary
        loss={"lossReport" in result ? result.lossReport : undefined}
      />
      {format !== 'xlsx' && <textarea
        className="code-input export-code"
        readOnly
        value={result.text}
        rows={16}
        aria-label="Generated export"
        spellCheck={false}
      />}
      <Notice>
        {format === 'xlsx' ? 'Editable Excel workbook with filters and frozen headers. User-authored text is stored as literal cell content. Constraints and notes distinguishes modeled metadata from preserved source evidence.' : 'Support is specific to the format, operation, and target profile. Generated SQL must be reviewed before use in a database.'}
      </Notice>
    </Modal>
  );
}
